/* eslint-disable react/jsx-no-bind */
/* eslint-disable prefer-destructuring */
/* eslint-disable no-unused-vars */
import * as React from 'react';
import {
  Users,
  Search,
  Filter,
  X,
  AlertCircle,
  Target,
  Settings2,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DateRangePicker,
  toUTCMidnight,
} from '@/components/ui/date-range-picker';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/ui/multi-select-button';
import { SingleSelect } from '@/components/ui/single-select';
import QualificationQuestionsPopper, {
  QualificationQuestion,
  QualificationFilter, // new slim type!
} from '@/components/conversation-insights/qualification-questions-button';
import QualityScore from '@/components/conversation-insights/filters/quality-score';

import type { AuditScoreRange } from '@/hooks/useConversationsInsights';
import { addDays, subDays } from 'date-fns';
import ResetIcon from '../../public/icons/new-dashboard/arrow-refresh-04.svg';
import ConcernCategoryIcon from '../../public/icons/new-dashboard/alert-triangle.svg';
import IntentIcon from '../../public/icons/new-dashboard/target-01.svg';
import SalesPitchGapIcon from '../../public/icons/new-dashboard/users-profiles-up.svg';
import StatusIcon from '../../public/icons/new-dashboard/alert-square.svg';
import AgentIcon from '../../public/icons/new-dashboard/user-profile-circle.svg';
import CustomerIcon from '../../public/icons/new-dashboard/user-profile-square.svg';
import FiltersIcon from '../../public/icons/new-dashboard/audio-settings-01.svg';

const today = new Date();
const thirtyAgo = subDays(today, 30);

export const CLEAN_FILTERS = {
  search_date: {
    start_date: toUTCMidnight(thirtyAgo),
    end_date: toUTCMidnight(today),
  },
  customer_id: undefined,
  conversation_owner_ids: [],
  crm_status_ids: [],
  concern_categories: [],
  agent_pitch_gaps: [],
  intents: [],
  qualification_questions: [],
  audit_score_range: undefined,
};

/* ═════════ props ═════════ */
interface FiltersBarProps {
  customersLoading: boolean;
  debounceSuggestionsWorkspace: (search: string) => void;
  customerOptions: MultiSelectOption[];
  agentOptions: MultiSelectOption[];
  statusOptions: MultiSelectOption[];
  concernOptions: MultiSelectOption[];
  pitchGapOptions: MultiSelectOption[];
  intentOptions: MultiSelectOption[];
  qualificationQuestions: QualificationQuestion[];

  defaultValue?: any;
  onChange?: (filters: any) => void;
  globalFilters?: any;
  onSubmit: Function;
  previousAppliedFilters: any;
  loadingOverview?: boolean;
  loadingIntentSpread?: boolean;
  loadingKeyInsights?: boolean;
  filters: any;
  setFilters: Function;
  showMoreFilters: boolean;
  setShowMoreFilters: Function;
}

/* ═════════ component ═════════ */
export default function FiltersBar({
  customersLoading,
  debounceSuggestionsWorkspace,
  customerOptions,
  agentOptions,
  statusOptions,
  concernOptions,
  pitchGapOptions,
  intentOptions,
  qualificationQuestions,
  defaultValue,
  onChange,
  globalFilters,
  onSubmit,
  previousAppliedFilters,
  loadingOverview,
  loadingIntentSpread,
  loadingKeyInsights,
  filters,
  setFilters,
  showMoreFilters,
  setShowMoreFilters,
}: FiltersBarProps) {
  /* ─── central state ─── */
  // const [filters, setFilters] = React.useState(defaultValue ?? CLEAN_FILTERS);

  /* generic setter */
  function update(key, value) {
    // setFilters((f) => ({ [k]: v }));
    setFilters(previousFilters => ({ ...previousFilters, [key]: value }));
  }

  function handleDateChange(r?: { from?: Date; to?: Date }) {
    const clicked = r?.to ?? r?.from;
    if (!clicked) {
      update('search_date', undefined);
      return;
    }

    const end = r?.to;
    const start = r?.from;
    if (end && start) {
      update('search_date', {
        start_date: start,
        end_date: end,
      });
    }
  }

  /* ─── qualification question adapter ─── */
  const handleQualificationUpdate = (
    id: string,
    value: string | number | boolean | string[],
    action: 'string' | 'number_min' | 'number_max' | 'bool' | 'reset',
  ) => {
    setFilters(prev => {
      const others = prev.qualification_questions.filter(
        q => q.capture_id !== id,
      );

      if (action === 'reset') {
        return { ...prev, qualification_questions: others };
      }

      let capture_output: QualificationFilter['capture_output'];

      switch (action) {
        case 'string':
          capture_output = value as string[];
          if (Array.isArray(capture_output) && capture_output.length === 0) {
            return { ...prev, qualification_questions: others };
          }
          break;
        case 'bool':
          capture_output = value as boolean;
          break;
        case 'number_min':
        case 'number_max': {
          const existingQuestion = prev.qualification_questions.find(
            q => q.capture_id === id,
          );
          const existingOutput = existingQuestion?.capture_output as
            | { min: number; max: number }
            | undefined;

          const min0 = existingOutput?.min ?? 0;
          const max0 = existingOutput?.max ?? 100;
          const newMin = action === 'number_min' ? (value as number) : min0;
          const newMax = action === 'number_max' ? (value as number) : max0;

          capture_output = { min: newMin, max: newMax };
          break;
        }
        default:
          return prev;
      }

      const updated: QualificationFilter = { capture_id: id, capture_output };
      return { ...prev, qualification_questions: [...others, updated] };
    });
  };

  const rangeValue = filters.search_date?.start_date
    ? {
        from: new Date(filters.search_date.start_date),
        to: filters.search_date.end_date
          ? new Date(filters.search_date.end_date)
          : undefined,
      }
    : undefined;

  const resetAll = () => {
    setShowMoreFilters(false);
    onSubmit(CLEAN_FILTERS);
    setFilters(CLEAN_FILTERS);
  };

  const isDirty = React.useMemo(() => {
    return JSON.stringify(previousAppliedFilters) !== JSON.stringify(filters);
  }, [filters, previousAppliedFilters]);

  /* emit upward */
  React.useEffect(() => {
    onChange?.(filters)}, [filters, onChange]);

  React.useEffect(() => {
    if (
      globalFilters?.intents?.length > 0 &&
      globalFilters?.intents !== filters?.intents
    ) {
      setFilters(prev => ({
        ...globalFilters,
        intents: globalFilters?.intents,
      }));
    }
  }, [globalFilters]);

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {/* Date range */}
        <DateRangePicker
          filters={filters}
          value={rangeValue}
          onChange={handleDateChange}
        />

        {/* Customer */}
        <SingleSelect
          placeholder="Customer"
          icon={<CustomerIcon className="size-4" />}
          options={customerOptions}
          value={filters.customer_id ? String(filters.customer_id) : undefined}
          onChange={v => update('customer_id', v ? Number(v) : undefined)}
          searchable
          onSearch={debounceSuggestionsWorkspace}
          isLoading={customersLoading}
        />

        {/* Agent */}
        <MultiSelect
          placeholder="Agent"
          icon={<AgentIcon className="size-4" />}
          options={agentOptions}
          value={filters.conversation_owner_ids.map(String)}
          onChange={ids => update('conversation_owner_ids', ids.map(Number))}
          searchable
        />

        {/* Status */}
        <MultiSelect
          placeholder="Status"
          icon={<StatusIcon className="size-4" />}
          options={statusOptions}
          value={filters.crm_status_ids.map(String)}
          onChange={ids => update('crm_status_ids', ids.map(Number))}
          showAvatar={false}
          searchable
        />

        {/* ─── More Filters ─── */}
        {showMoreFilters && (
          <>
            <MultiSelect
              placeholder="Concern Category"
              icon={<ConcernCategoryIcon />}
              options={concernOptions}
              value={filters.concern_categories}
              onChange={v => update('concern_categories', v)}
              searchable
              showAvatar={false}
            />

            <MultiSelect
              placeholder="Sales Pitch Gaps"
              icon={<SalesPitchGapIcon />}
              options={pitchGapOptions}
              value={filters.agent_pitch_gaps}
              onChange={v => update('agent_pitch_gaps', v)}
              searchable
              showAvatar={false}
            />

            <MultiSelect
              placeholder="Intent"
              icon={<IntentIcon className="size-4" />}
              options={intentOptions}
              value={filters.intents}
              onChange={v => update('intents', v)}
              searchable={false}
              showAvatar={false}
            />

            <QualificationQuestionsPopper
              currentValue={filters.qualification_questions}
              qualificationQuestions={qualificationQuestions}
              handleQuestionFilterUpdate={handleQualificationUpdate}
            />

            <QualityScore
              value={filters.audit_score_range}
              onChange={(range: AuditScoreRange | undefined) =>
                update('audit_score_range', range)
              }
            />
          </>
        )}

        {/* Toggle & reset */}
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-muted"
          onClick={() => setShowMoreFilters(p => !p)}
        >
          <FiltersIcon className="size-4" />
          {showMoreFilters ? 'Less Filters' : 'More Filters'}
          {showMoreFilters ? (
            <ChevronLeft size={16} color="#71717A" />
          ) : (
            <ChevronRight size={16} color="#71717A" />
          )}
        </Button>

        {!loadingIntentSpread && isDirty && (
          <>
            <Button variant="default" size="sm" onClick={() => onSubmit()}>
              Apply Filter
            </Button>
          </>
        )}
      </div>

      <Button variant="ghost" size="sm" onClick={resetAll}>
        <ResetIcon className="size-4 mr-1" /> Reset All
      </Button>
    </div>
  );
}
